import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  TablePagination,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Face as FaceIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useFavicon } from '../useFavicon';
import sessionService from '../api/sessionService';
import ReferencePersonsList from '../components/ReferencePersonsList';
import faceRecognitionService from '../services/faceRecognitionService';
import AuthenticatedImage from '../components/AuthenticatedImage';
import AuthenticatedAvatar from '../components/AuthenticatedAvatar';

// Define the favicon for Face Recognition page
const FACE_RECOGNITION_FAVICON_URL = process.env.PUBLIC_URL + "/face-recognition.svg"; // You may need to create this icon

/**
 * Face Recognition page for managing reference persons and finding matches
 */
const FaceRecognitionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [images, setImages] = useState([]);
  const [referencePersons, setReferencePersons] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Pagination states for Tag New Person tab
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalImages, setTotalImages] = useState(0);
  const [loadingImages, setLoadingImages] = useState(false);
  
  // Matches tab states
  const [selectedPersonMatches, setSelectedPersonMatches] = useState({});
  const [loadingMatches, setLoadingMatches] = useState({});
  const [matchesPage, setMatchesPage] = useState({});
  const [matchesRowsPerPage, setMatchesRowsPerPage] = useState({});

  // Set favicon
  useFavicon(FACE_RECOGNITION_FAVICON_URL);

  // Load session data
  useEffect(() => {
    const loadSessionData = async () => {
      setLoading(true);
      
      try {
        if (!sessionId) {
          setError('No session selected. Please select a session first.');
          setLoading(false);
          return;
        }
        
        // Load session details
        const sessionData = await sessionService.getSession(sessionId);
        setSession(sessionData);
        
        // Load reference persons
        const referenceData = await faceRecognitionService.getSessionReferencePersons(sessionId);
        setReferencePersons(referenceData.referencePersons || []);
        
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load session data');
        setLoading(false);
      }
    };
    
    loadSessionData();
  }, [sessionId, refreshTrigger]);

  // Load images for Tag New Person tab with pagination
  useEffect(() => {
    const loadImages = async () => {
      if (tabValue !== 1 || !sessionId) return;
      
      setLoadingImages(true);
      try {
        const imagesResponse = await sessionService.getSessionImages(sessionId, {
          page: page + 1,
          limit: rowsPerPage,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });
        setImages(imagesResponse.images || []);
        setTotalImages(imagesResponse.pagination?.total || 0);
      } catch (err) {
        console.error('Error loading images:', err);
      }
      setLoadingImages(false);
    };
    
    loadImages();
  }, [sessionId, tabValue, page, rowsPerPage]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      setPage(0); // Reset pagination when switching to Tag New Person tab
    }
  };
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Load matches for a reference person
  const loadMatchesForPerson = async (personId, personName) => {
    if (selectedPersonMatches[personId]) {
      // Already loaded, toggle display
      setSelectedPersonMatches(prev => {
        const updated = { ...prev };
        delete updated[personId];
        return updated;
      });
      setMatchesPage(prev => {
        const updated = { ...prev };
        delete updated[personId];
        return updated;
      });
      setMatchesRowsPerPage(prev => {
        const updated = { ...prev };
        delete updated[personId];
        return updated;
      });
      return;
    }
    
    setLoadingMatches(prev => ({ ...prev, [personId]: true }));
    try {
      const response = await faceRecognitionService.findPersonMatches(sessionId, personId);
      setSelectedPersonMatches(prev => ({
        ...prev,
        [personId]: response.matches || []
      }));
      // Initialize pagination for this person
      setMatchesPage(prev => ({ ...prev, [personId]: 0 }));
      setMatchesRowsPerPage(prev => ({ ...prev, [personId]: 12 }));
    } catch (err) {
      console.error('Error loading matches:', err);
      alert(`Failed to load matches for ${personName}`);
    }
    setLoadingMatches(prev => ({ ...prev, [personId]: false }));
  };
  
  // Handle pagination for matches
  const handleMatchesPageChange = (personId, event, newPage) => {
    setMatchesPage(prev => ({ ...prev, [personId]: newPage }));
  };
  
  const handleMatchesRowsPerPageChange = (personId, event) => {
    setMatchesRowsPerPage(prev => ({ ...prev, [personId]: parseInt(event.target.value, 10) }));
    setMatchesPage(prev => ({ ...prev, [personId]: 0 }));
  };

  // Handle refresh of reference persons list
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/image-upload/${sessionId || ''}`)}
            startIcon={<PhotoLibraryIcon />}
          >
            Go to Images
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <FaceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Face Recognition
        </Typography>
        
        {session && (
          <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.paper' }}>
            <Box display="flex" alignItems="center">
              <Box flex={1}>
                <Typography variant="h6" color="primary">
                  {session.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {session.description || 'No description'}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                  <Typography variant="body2">
                    <strong>Images:</strong> {session.formattedStats?.totalImages || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {session.status}
                  </Typography>
                </Box>
              </Box>
              <Button 
                variant="outlined" 
                onClick={() => navigate(`/image-upload/${sessionId}`)}
                startIcon={<FolderIcon />}
              >
                Go to Session
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="face recognition tabs"
        >
          <Tab label="Reference Persons" id="tab-0" />
          <Tab label="Tag New Person" id="tab-1" />
          <Tab label="View Matches" id="tab-2" />
          <Tab label="About Face Recognition" id="tab-3" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box role="tabpanel" hidden={tabValue !== 0}>
        {tabValue === 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                onClick={handleRefresh}
                variant="outlined"
                size="small"
              >
                Refresh List
              </Button>
            </Box>
            <ReferencePersonsList sessionId={sessionId} />
          </>
        )}
      </Box>

      <Box role="tabpanel" hidden={tabValue !== 1}>
        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select an Image to Tag a Person
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select a photo from your session to tag a person. The tagged person will be used as a reference to find them across all photos.
            </Typography>
            
            {loadingImages ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : images.length === 0 ? (
              <Alert severity="info">
                No images available in this session. Please upload images first.
              </Alert>
            ) : (
              <>
                <Grid container spacing={2}>
                  {images.map((image) => (
                    <Grid item xs={12} sm={6} md={3} key={image.uniqueId}>
                      <Card>
                        <AuthenticatedImage
                          imageId={image.uniqueId}
                          type="thumbnail"
                          fallbackToServe={true}
                          alt={image.name}
                          sx={{
                            width: '100%',
                            height: 160,
                            objectFit: 'cover',
                          }}
                        />
                        <CardContent>
                          <Typography variant="body2" noWrap>
                            {image.name}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              fullWidth
                              onClick={() => {
                                navigate(`/images?session=${sessionId}&tagImage=${image.uniqueId}`);
                              }}
                            >
                              Tag Person
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <TablePagination
                  component="div"
                  count={totalImages}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[12, 24, 48, 96]}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </Box>
        )}
      </Box>

      <Box role="tabpanel" hidden={tabValue !== 2}>
        {tabValue === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              View All Matches by Reference Person
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Click on a reference person to view all photos where they appear.
            </Typography>
            
            {referencePersons.length === 0 ? (
              <Alert severity="info">
                No reference persons tagged yet. Go to the "Tag New Person" tab to get started.
              </Alert>
            ) : (
              <Box>
                {referencePersons.map((person) => {
                  // Get primary reference photo for the person
                  const primaryPhoto = person.referencePhotoDetails?.find(p => p.isPrimary) || 
                                      person.referencePhotoDetails?.[0];
                  
                  return (
                  <Accordion 
                    key={person._id}
                    expanded={!!selectedPersonMatches[person._id]}
                    onChange={() => loadMatchesForPerson(person._id, person.personName)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <AuthenticatedAvatar
                          imageId={primaryPhoto?.imageId || person.primaryImage?.imageId}
                          alt={person.personName}
                          sx={{ width: 50, height: 50 }}
                        />
                        <Box flex={1}>
                          <Typography variant="subtitle1">
                            {person.personName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip 
                              size="small" 
                              label={`${person.stats?.totalReferencePhotos || 0} reference photos`}
                              color="primary"
                              variant="outlined"
                            />
                            <Chip 
                              size="small" 
                              label={`${person.stats?.totalMatches || 0} total matches`}
                              color="secondary"
                              variant="outlined"
                            />
                            <Chip 
                              size="small" 
                              label={`${person.stats?.imagesWithMatches || 0} images`}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {loadingMatches[person._id] ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : selectedPersonMatches[person._id] ? (
                        <Box>
                          {selectedPersonMatches[person._id].length === 0 ? (
                            <Alert severity="info">
                              No matches found yet. Run face recognition to find matches.
                            </Alert>
                          ) : (
                            <>
                              <Grid container spacing={2}>
                                {selectedPersonMatches[person._id]
                                  .slice(
                                    (matchesPage[person._id] || 0) * (matchesRowsPerPage[person._id] || 12),
                                    ((matchesPage[person._id] || 0) + 1) * (matchesRowsPerPage[person._id] || 12)
                                  )
                                  .map((match, idx) => (
                                    <Grid item xs={12} sm={6} md={3} key={`${match.imageId}-${idx}`}>
                                      <Card>
                                        <AuthenticatedImage
                                          imageId={match.imageId}
                                          type="thumbnail"
                                          fallbackToServe={true}
                                          alt={match.imageName || 'Match'}
                                          sx={{
                                            width: '100%',
                                            height: 160,
                                            objectFit: 'cover',
                                          }}
                                        />
                                        <CardContent>
                                          <Typography variant="body2" noWrap>
                                            {match.imageName || 'Unknown'}
                                          </Typography>
                                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {match.confidence && (
                                              <Chip 
                                                size="small" 
                                                label={`${Math.round(match.confidence)}% match`}
                                                color={match.confidence > 90 ? 'success' : 'default'}
                                              />
                                            )}
                                            {match.isReferencePerson && (
                                              <Chip 
                                                size="small" 
                                                label="Reference"
                                                color="primary"
                                              />
                                            )}
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                              </Grid>
                              <TablePagination
                                component="div"
                                count={selectedPersonMatches[person._id].length}
                                page={matchesPage[person._id] || 0}
                                onPageChange={(e, newPage) => handleMatchesPageChange(person._id, e, newPage)}
                                rowsPerPage={matchesRowsPerPage[person._id] || 12}
                                onRowsPerPageChange={(e) => handleMatchesRowsPerPageChange(person._id, e)}
                                rowsPerPageOptions={[12, 24, 48, 96]}
                                sx={{ mt: 2 }}
                              />
                            </>
                          )}
                        </Box>
                      ) : null}
                    </AccordionDetails>
                  </Accordion>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box role="tabpanel" hidden={tabValue !== 3}>
        {tabValue === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              About Face Recognition
            </Typography>
            <Paper sx={{ p: 3 }}>
              <Typography variant="body1" paragraph>
                Face recognition uses Amazon Rekognition to identify people across multiple photos in your session.
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                How it works:
              </Typography>
              
              <ol>
                <li>
                  <Typography variant="body1" paragraph>
                    <strong>Tag a person</strong> - Select an image and tag a person in it by giving them a name
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" paragraph>
                    <strong>Find matches</strong> - Our system will analyze all photos in the session to find the same person
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" paragraph>
                    <strong>View results</strong> - See all instances where the tagged person appears across your photos
                  </Typography>
                </li>
              </ol>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Privacy Information:
              </Typography>
              <Typography variant="body2" paragraph>
                All face recognition is performed securely on your session photos only. Face data is not stored or used for any other purpose beyond finding matches within your session.
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default FaceRecognitionPage;
